import type { StagedActionJob } from '@renderer/overlay/hooks/use-staged-action-jobs'

export interface JobStatusSnapshot {
  jobs: Array<StagedActionJob>
  /** Server clock at publish. Absent from backends that predate the stamp. */
  publishedAt?: number
}

export type JobStatusReconciliation =
  | { stale: true }
  | {
      stale: false
      jobs: Array<StagedActionJob>
      lastPublishedAt: number | undefined
      /** A job left the active set or arrived terminal, so its actions need a refetch. */
      aJobFinished: boolean
    }

const isTerminal = (status: string) => status === 'completed' || status === 'error'

/**
 * Each broadcast is a full snapshot of the active jobs, and the Pub/Sub hop
 * to the socket doesn't keep publish order. Two snapshots published
 * milliseconds apart can arrive swapped, which pins a finished job as
 * generating for good. Anything older than the last applied snapshot is dropped.
 */
export function reconcileJobStatusSnapshot(
  prevJobs: Array<StagedActionJob> | undefined,
  lastPublishedAt: number | undefined,
  incoming: JobStatusSnapshot,
): JobStatusReconciliation {
  if (incoming.publishedAt !== undefined && lastPublishedAt !== undefined && incoming.publishedAt < lastPublishedAt) {
    return { stale: true }
  }

  // A finished job may have created actions whose own `updated` broadcast was
  // dropped, so the refetch keys off two signals. A job missing from the
  // snapshot finished, since the backend lists only queued and generating jobs.
  // A job arriving already terminal covers a window that connected mid-flight
  // and never saw it active. Only newly terminal jobs count, or a lingering
  // entry would refetch on every message.
  const nextIds = new Set(incoming.jobs.map((job) => job.id))
  const prevTerminalIds = new Set((prevJobs ?? []).filter((job) => isTerminal(job.status)).map((job) => job.id))
  const aJobDisappeared = (prevJobs ?? []).some((job) => !nextIds.has(job.id))
  const aJobNewlyTerminal = incoming.jobs.some((job) => isTerminal(job.status) && !prevTerminalIds.has(job.id))

  return {
    stale: false,
    jobs: incoming.jobs,
    lastPublishedAt: incoming.publishedAt ?? lastPublishedAt,
    aJobFinished: aJobDisappeared || aJobNewlyTerminal,
  }
}
